import { api } from './client'
import type { Equipment } from '@/types/equipment'

function validateEquipmentPayload(data: Partial<Equipment>): string | null {
  const requiredFields = [
    'name',
    'type',
    'status',
    'purchase_date',
    'requires_fuel',
    'model',
    'serial_number',
  ]
  for (const field of requiredFields) {
    if (
      data[field as keyof Equipment] === undefined ||
      data[field as keyof Equipment] === null ||
      (typeof data[field as keyof Equipment] === 'string' && data[field as keyof Equipment] === '')
    ) {
      return `Missing required field: ${field}`
    }
  }
  return null
}

export const equipmentApi = {
  async getAll(): Promise<Equipment[]> {
    console.log('[equipmentApi] getAll called')
    try {
      // Add a reasonable timeout so the UI fails fast instead of hanging indefinitely.
      const response = await api.get('/equipment/equipment/', { timeout: 10000 })
      const data = response.data as unknown

      // Normalize common API shapes so callers always receive an array.
      if (Array.isArray(data)) return data as Equipment[]

      if (data && typeof data === 'object') {
        const obj = data as Record<string, unknown>
        if (Array.isArray(obj.results)) return obj.results as Equipment[]
        if (Array.isArray(obj.data)) return obj.data as Equipment[]
        if (Array.isArray(obj.items)) return obj.items as Equipment[]

        // Fallback: first array-valued property
        for (const key of Object.keys(obj)) {
          const val = obj[key]
          if (Array.isArray(val)) return val as Equipment[]
        }
      }

      // Unrecognized shape -> return empty array to keep UI stable.
      console.warn('[equipmentApi] getAll: response shape unexpected, returning empty array', data)
      return []
    } catch (error) {
      console.error('[equipmentApi] getAll error:', error)
      return []
    }
  },

  async getById(id: number): Promise<Equipment> {
    const response = await api.get(`/equipment/equipment/${id}/`)
    return response.data
  },

  async create(
    data: Partial<Equipment>,
  ): Promise<{ success: boolean; message: string; equipment?: Equipment }> {
    const validationError = validateEquipmentPayload(data)
    if (validationError) {
      return { success: false, message: validationError }
    }
    try {
      console.log('[equipmentApi] POST payload:', data)
      const response = await api.post('/equipment/equipment/', data)
      const createdObj = Array.isArray(response.data) ? response.data[response.data.length - 1] : response.data
      return { success: true, message: 'Equipment created successfully.', equipment: createdObj }
    } catch (error: unknown) {
      let errorMsg = 'Failed to create equipment.'
      if (typeof error === 'object' && error !== null) {
        const errObj = error as Record<string, unknown>
        const response = errObj.response as Record<string, unknown> | undefined
        const respData = response?.data as Record<string, unknown> | undefined
        if (respData && respData.detail) {
          errorMsg = String(respData.detail)
        } else if (errObj.message) {
          errorMsg = String(errObj.message)
        }
      } else if (typeof error === 'string') {
        errorMsg = error
      }
      return { success: false, message: errorMsg }
    }
  },

  async update(
    id: number,
    data: Partial<Equipment>,
  ): Promise<{ success: boolean; message: string; equipment?: Equipment }> {
    try {
      const response = await api.patch(`/equipment/equipment/${id}/`, data)
      return { success: true, message: 'Equipment updated successfully.', equipment: response.data }
    } catch (error: unknown) {
      let errorMsg = 'Failed to update equipment.'
      if (typeof error === 'object' && error !== null) {
        const errObj = error as Record<string, unknown>
        const response = errObj.response as Record<string, unknown> | undefined
        const respData = response?.data as Record<string, unknown> | undefined
        if (respData && respData.detail) {
          errorMsg = String(respData.detail)
        } else if (errObj.message) {
          errorMsg = String(errObj.message)
        }
      } else if (typeof error === 'string') {
        errorMsg = error
      }
      return { success: false, message: errorMsg }
    }
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/equipment/equipment/${id}/`)
  },
}
